import Http from 'services/Http';
import { preparePublicKeyCredential, preparePublicKeyOptions } from 'utils/webAuthn';

class Passkey {
  async fetchOwnCredentials(companyId) {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    const response = await Http.get('/v1/webauthn/credentials', {
      headers: { 'x-company': companyId },
    });

    return response.data?.credentials || [];
  }

  /**
   * Fetch existing passkey credentials for a user
   *
   * @param {string} companyId - The company ID
   * @param {string} employeeId - The employee ID
   * @returns {Promise<Array>} - Array of credential objects
   */
  async fetchCredentials(companyId, employeeId) {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    const response = await Http.get(`/v1/employee/${employeeId}/webauthncredentials`, {
      headers: { 'x-company': companyId },
    });

    return response.data?.credentials || [];
  }

  /**
   * Send an SMS code before registering a settings passkey
   *
   * @param {string} companyId
   * @returns {Promise<void>}
   */
  async sendRegistrationOtp(companyId) {
    if (!companyId) {
      throw new Error('Company ID is required');
    }
    await Http.post('/v1/webauthn/register/send-otp', {}, { headers: { 'x-company': companyId } });
  }

  /**
   * Verify the SMS code for settings passkey registration
   *
   * @param {string} companyId
   * @param {string} code
   * @returns {Promise<void>}
   */
  async verifyRegistrationOtp(companyId, code) {
    if (!companyId || !code) {
      throw new Error('Company ID and code are required');
    }
    await Http.post(
      '/v1/webauthn/register/verify-otp',
      { code },
      { headers: { 'x-company': companyId } },
    );
  }

  /**
   * Register a new passkey from authenticated Settings.
   *
   * @param {Object} params - Parameters for registering a passkey
   * @param {string} params.companyId - The company ID
   * @param {string} params.userId - The user ID
   * @param {string} params.username - The username or email
   * @returns {Promise<void>}
   */
  async registerPasskey({ companyId, userId, username }) {
    if (!companyId || !userId) {
      throw new Error('Company ID and User ID are required');
    }

    return this.completeRegistration({
      userId,
      username,
      beginPath: '/v1/webauthn/register/begin',
      completePath: '/v1/webauthn/register/complete',
      config: { headers: { 'x-company': companyId } },
    });
  }

  /**
   * Register a new passkey immediately after an SMS login.
   *
   * @param {Object} params - Registration parameters
   * @param {string} params.companyId - The backend proof-company ID
   * @param {string} params.userId - The user ID
   * @param {string} params.username - The username or email
   * @param {string} params.passkeyEnrollmentHandoff - Opaque server-bound enrollment capability
   * @returns {Promise<void>}
   */
  async registerPasskeyWithEnrollmentHandoff({
    companyId,
    userId,
    username,
    passkeyEnrollmentHandoff,
  }) {
    if (!companyId || !userId || !passkeyEnrollmentHandoff) {
      throw new Error('Company ID, User ID, and enrollment handoff are required');
    }

    const headers = {
      'x-company': companyId,
      'x-passkey-enrollment-handoff': passkeyEnrollmentHandoff,
    };
    // Same-origin auth cookies can still accompany this request. These public endpoints authorize
    // only the opaque, server-bound handoff; this prevents the client from adding a bearer header.
    return this.completeRegistration({
      userId,
      username,
      beginPath: '/v1/webauthn/register/handoff/begin',
      completePath: '/v1/webauthn/register/handoff/complete',
      config: { headers, skipAuthorization: true },
    });
  }

  async completeRegistration({ userId, username, beginPath, completePath, config }) {
    const response = await Http.post(
      beginPath,
      {
        userId,
        username,
      },
      config,
    );

    const requestJson = response.data;

    if (requestJson?.otpRequired) {
      throw Object.assign(new Error('otpRequired'), { code: 'otpRequired' });
    }

    if (!requestJson?.options) {
      throw new Error('Invalid registration options from server');
    }

    // 2. Prepare options for WebAuthn API
    const publicKeyOptions = preparePublicKeyOptions(requestJson.options);

    // 3. Call browser's credential creation API
    const credential = await navigator.credentials.create({
      publicKey: publicKeyOptions,
    });

    // 4. Prepare credential for server
    const processedCredential = preparePublicKeyCredential(credential);

    // 5. Complete registration on server
    await Http.post(completePath, processedCredential, config);
  }

  /**
   * Delete a passkey credential
   *
   * @param {string} companyId - The company ID
   * @param {string} credentialId - The credential ID to delete
   * @returns {Promise<void>}
   */
  async deleteCredential(companyId, credentialId) {
    if (!companyId || !credentialId) {
      throw new Error('Company ID and Credential ID are required');
    }

    await Http.delete(`/v1/webauthn/credentials/${credentialId}`, {
      headers: { 'x-company': companyId },
    });
  }
}

export default new Passkey();
